import { useEffect, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "@/components/ui/sheet";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Lock, Unlock, Power, KeyRound, LogOut } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ROLES, ROLE_LABEL } from "@/lib/constants";
import { useAuth } from "@/contexts/AuthContext";

const EMPTY = {
  username: "",
  email: "",
  full_name: "",
  role: "sales_executive",
  password: "",
};

const Input = (props) => (
  <input {...props}
    className={
      "w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm " +
      "focus:ring-1 focus:ring-[var(--theme-color)] focus:border-[var(--theme-color)] outline-none " + (props.className || "")
    } />
);

const Field = ({ label, children, required }) => (
  <div>
    <label className="text-label block mb-1.5">
      {label}{required && <span className="text-destructive"> *</span>}
    </label>
    {children}
  </div>
);

export default function Users() {
  const { user: me } = useAuth();
  const isMaster = me?.role === "master_admin";

  const [users, setUsers] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [resetPwdFor, setResetPwdFor] = useState(null);
  const [newPwd, setNewPwd] = useState("");

  const fetch = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { fetch(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDrawerOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      username: u.username,
      email: u.email || "",
      full_name: u.full_name,
      role: u.role,
      password: "",
    });
    setDrawerOpen(true);
  };

  const save = async () => {
    setLoading(true);
    try {
      if (editing) {
        const payload = {
          full_name: form.full_name,
          email: form.email || null,
          role: form.role,
        };
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setDrawerOpen(false);
      await fetch();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "User disabled" : "User enabled");
      fetch();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleLock = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_locked: !u.is_locked });
      toast.success(u.is_locked ? "Account unlocked" : "Account locked");
      fetch();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const forceLogout = async (u) => {
    try {
      await api.post(`/users/${u.id}/force-logout`);
      toast.success(`${u.full_name} signed out`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const submitPwdReset = async () => {
    if (!newPwd || newPwd.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    try {
      await api.patch(`/users/${resetPwdFor.id}`, { password: newPwd });
      toast.success("Password reset");
      setResetPwdFor(null);
      setNewPwd("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 sm:p-8 space-y-6" data-testid="users-page">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-label">Access</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold mt-1">Users</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {users.length} {users.length === 1 ? "user" : "users"}
          </p>
        </div>
        {isMaster && (
          <button
            onClick={openNew}
            data-testid="new-user-btn"
            className="text-white text-sm font-medium rounded-sm py-2 px-3.5 hover:opacity-90 flex items-center gap-1.5"
            style={{ backgroundColor: "var(--theme-color)" }}
          >
            <Plus className="w-4 h-4" /> New user
          </button>
        )}
      </header>

      <section className="border border-border rounded-sm bg-background overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40">
              <tr className="text-left">
                <Th>Name</Th><Th>Username</Th><Th>Email</Th><Th>Role</Th>
                <Th>Status</Th><Th>Last Login</Th>
                {isMaster && <Th className="text-right">Actions</Th>}
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={isMaster ? 7 : 6} className="text-center py-10 text-muted-foreground">No users.</td></tr>
              )}
              {users.map((u) => (
                <tr key={u.id} className="border-t border-border" data-testid={`user-row-${u.id}`}>
                  <Td>
                    <button
                      onClick={() => isMaster && openEdit(u)}
                      className="font-medium hover:underline disabled:no-underline"
                      disabled={!isMaster}
                    >
                      {u.full_name}
                    </button>
                  </Td>
                  <Td className="mono text-xs">{u.username}</Td>
                  <Td className="text-xs">{u.email || "—"}</Td>
                  <Td>
                    <span className="px-2 py-0.5 text-xs rounded-sm bg-secondary text-secondary-foreground">
                      {ROLE_LABEL[u.role]}
                    </span>
                  </Td>
                  <Td>
                    {u.is_locked ? (
                      <span className="text-xs text-destructive font-medium">Locked</span>
                    ) : u.is_active ? (
                      <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Active</span>
                    ) : (
                      <span className="text-xs text-muted-foreground font-medium">Disabled</span>
                    )}
                  </Td>
                  <Td className="text-xs text-muted-foreground mono">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
                  </Td>
                  {isMaster && u.id !== me.id && (
                    <Td className="text-right whitespace-nowrap">
                      <IconBtn onClick={() => setResetPwdFor(u)} title="Reset password"
                        testId={`reset-pwd-${u.id}`}><KeyRound className="w-4 h-4" /></IconBtn>
                      <IconBtn onClick={() => toggleLock(u)} title={u.is_locked ? "Unlock" : "Lock"}
                        testId={`toggle-lock-${u.id}`}>
                        {u.is_locked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                      </IconBtn>
                      <IconBtn onClick={() => toggleActive(u)} title={u.is_active ? "Disable" : "Enable"}
                        testId={`toggle-active-${u.id}`}><Power className="w-4 h-4" /></IconBtn>
                      <IconBtn onClick={() => forceLogout(u)} title="Force logout"
                        testId={`force-logout-${u.id}`}><LogOut className="w-4 h-4" /></IconBtn>
                    </Td>
                  )}
                  {isMaster && u.id === me.id && <Td className="text-right text-xs text-muted-foreground">You</Td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Create / Edit drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{editing ? "Edit user" : "Create user"}</SheetTitle>
            <SheetDescription>
              {editing ? "Update profile and role." : "Master Admin sets the initial password."}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-4">
            <Field label="Full name" required>
              <Input value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                data-testid="user-fullname-input" />
            </Field>
            <Field label="Username" required>
              <Input value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                disabled={!!editing} data-testid="user-username-input" />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="user-email-input" />
            </Field>
            <Field label="Role" required>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            {!editing && (
              <Field label="Password" required>
                <Input type="password" value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  data-testid="user-password-input" />
              </Field>
            )}
          </div>
          <SheetFooter className="mt-6">
            <button
              onClick={save}
              disabled={loading || !form.full_name || !form.username || (!editing && !form.password)}
              data-testid="save-user-btn"
              className="text-white text-sm font-medium rounded-sm py-2 px-4 hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: "var(--theme-color)" }}
            >
              {editing ? "Save changes" : "Create user"}
            </button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Password reset modal (sheet) */}
      <Sheet open={!!resetPwdFor} onOpenChange={(v) => !v && setResetPwdFor(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Reset password</SheetTitle>
            <SheetDescription>
              Set a new password for {resetPwdFor?.full_name}.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-4">
            <Field label="New password" required>
              <Input type="text" value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
                placeholder="Min 6 characters" data-testid="reset-pwd-input" />
            </Field>
            <p className="text-xs text-muted-foreground">
              The user will receive this password out-of-band from you.
            </p>
          </div>
          <SheetFooter className="mt-6">
            <button
              onClick={submitPwdReset}
              data-testid="reset-pwd-submit"
              className="text-white text-sm font-medium rounded-sm py-2 px-4 hover:opacity-90"
              style={{ backgroundColor: "var(--theme-color)" }}
            >
              Reset password
            </button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }
function IconBtn({ children, onClick, title, testId }) {
  return (
    <button onClick={onClick} title={title} data-testid={testId}
      className="p-1.5 rounded-sm text-muted-foreground hover:text-foreground hover:bg-secondary inline-flex">
      {children}
    </button>
  );
}
